import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { allArticles } from '@/mocks/articles';

const categoryGroups = [
  { label: 'Vêtements', cats: categories.slice(0, 5) },
  { label: 'Accessoires', cats: categories.slice(5, 10) },
  { label: 'Autres', cats: categories.slice(10) },
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileCategoryGroup, setMobileCategoryGroup] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, []);

  const recentArticles = allArticles.slice(0, 5);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-xl border-b border-background-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-background-50/90 backdrop-blur-sm border-b border-background-200/40'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="text-lg md:text-xl font-heading font-bold text-foreground-900 tracking-tight whitespace-nowrap hover:text-primary-600 transition-colors cursor-pointer"
          >
            Hipobuy Spreadsheet
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="px-3 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Accueil
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-300/50 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-menu-line"></i>
                <span>Catégories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200/80 overflow-hidden animate-slide-down">
                  {categoryGroups.map((group, gIdx) => (
                    <div key={group.label} className={gIdx > 0 ? 'border-t border-background-100' : ''}>
                      <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider bg-background-100/50">
                        {group.label}
                      </p>
                      {group.cats.map((cat) => (
                        <button
                          key={cat.name}
                          onClick={() => handleCategoryClick(cat.url)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors text-left whitespace-nowrap cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                            <i className={cat.icon}></i>
                          </span>
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/guide-complet-hipobuy-spreadsheet"
              className="px-3 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-guide-line mr-1.5"></i>
              Guide Shopping
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              hipobuy spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full border-2 border-primary-400 text-primary-700 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy it Now
            </a>
          </div>

          <button
            onClick={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
              setMobileCategoryGroup(null);
            }}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-slide-down max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-home-4-line"></i>
              </span>
              Accueil
            </Link>

            <Link
              to="/article/guide-complet-hipobuy-spreadsheet"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-guide-line"></i>
              </span>
              Guide Shopping
            </Link>

            <div className="border-t border-background-200/60 pt-2 mt-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 py-1">
                Catégories Mode
              </p>

              {categoryGroups.map((group) => (
                <div key={group.label} className="mb-1">
                  <button
                    onClick={() => setMobileCategoryGroup(mobileCategoryGroup === categoryGroups.indexOf(group) ? null : categoryGroups.indexOf(group))}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-foreground-500 uppercase tracking-wide hover:text-foreground-700 transition-colors cursor-pointer"
                  >
                    {group.label}
                    <i className={`ri-arrow-down-s-line transition-transform text-sm ${mobileCategoryGroup === categoryGroups.indexOf(group) ? 'rotate-180' : ''}`}></i>
                  </button>
                  {mobileCategoryGroup === categoryGroups.indexOf(group) && (
                    <div className="animate-slide-down">
                      {group.cats.map((cat) => (
                        <button
                          key={cat.name}
                          onClick={() => handleCategoryClick(cat.url)}
                          className="w-full flex items-center gap-3 pl-8 pr-3 py-2.5 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-800 transition-colors text-left whitespace-nowrap cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                            <i className={cat.icon}></i>
                          </span>
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-background-200/60 pt-2 mt-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 py-1">
                Articles Récents
              </p>
              {recentArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-800 transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-article-line"></i>
                  </span>
                  <span className="truncate">{a.title}</span>
                </Link>
              ))}
            </div>

            <div className="pt-3 flex flex-col gap-2 border-t border-background-200/60 mt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                hipobuy spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 rounded-full border-2 border-primary-400 text-primary-700 text-sm font-semibold text-center hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy it Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}